import React, {ChangeEvent, CSSProperties, useEffect, useRef, useState} from "react";
import {polarToCartesian} from "../../../common/math";
import {AnimatePresence, motion} from "framer-motion";
import {DraggableCore, DraggableEventHandler} from "react-draggable";
import store from "../../../store";
import {useStyle} from "../../../hooks";
import {useZoom} from "../../workspace/ZoomTranslate";
import {InputBase} from "@mui/material";

const getTrianglePath = (x: number, y: number, r: number, a: number) => {
    const a0 = a;
    const a1 = a - 120;
    const a2 = a + 120;
    const p0 = polarToCartesian(x, y, r, a0);
    const p1 = polarToCartesian(x, y, r, a1);
    const p2 = polarToCartesian(x, y, r, a2);
    return `M ${p0.x} ${p0.y} L ${p1.x} ${p1.y} L ${p2.x} ${p2.y} Z`;
}

const getTriangle = (x: number, y: number, r: number, a: number): Triangle => {
    const a0 = a;
    const a1 = a - 120;
    const a2 = a + 120;
    const p0 = polarToCartesian(x, y, r, a0);
    const p1 = polarToCartesian(x, y, r, a1);
    const p2 = polarToCartesian(x, y, r, a2);
    return [p0, p1, p2];
}

const getTriangleClipPath = (x: number, y: number, r: number, a: number) => {
    return getTriangle(x, y, r, a).map(p => p.x + "% " + p.y + "%").join(", ");
}

const getCircleHoleClipPath = (radius: number) => {
    let precision = 64;
    let c = [...Array(precision)].map((_, i) => {
        let a = -i / (precision - 1) * Math.PI * 2;
        let x = Math.cos(a) * radius + 50;
        let y = Math.sin(a) * radius + 50;
        return `${x}% ${y}%`
    })

    return `100% 50%, 100% 100%, 0 100%, 0 0, 100% 0, 100% 50%, ${c.join(',')}`;
}

const getColorWheelSteps = (n: number, s: number = 100, l: number = 50) => {
    const a = 360 / n;
    const steps = Array<string>(n);
    for (let i = 0; i <= n; i++) {
        steps[i] = `hsl(${i * a}, ${s}%, ${l}%)`;
    }
    return steps;
}

type Point = { x: number, y: number };
type Line = [Point, Point];
type Triangle = [Point, Point, Point];

function distance(point1: Point, point2: Point): number {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    return Math.sqrt(dx * dx + dy * dy);
}

function pointInsideTriangle(point: Point, triangle: Triangle): boolean {
    const [p1, p2, p3] = triangle;

    // Calculate area of triangle ABC
    const areaABC = 0.5 * Math.abs((p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y)));

    // Calculate area of triangle PBC
    const areaPBC = 0.5 * Math.abs((point.x * (p2.y - p3.y) + p2.x * (p3.y - point.y) + p3.x * (point.y - p2.y)));

    // Calculate area of triangle PAC
    const areaPAC = 0.5 * Math.abs((p1.x * (point.y - p3.y) + point.x * (p3.y - p1.y) + p3.x * (p1.y - point.y)));

    // Calculate area of triangle PAB
    const areaPAB = 0.5 * Math.abs((p1.x * (p2.y - point.y) + p2.x * (point.y - p1.y) + point.x * (p1.y - p2.y)));

    // Check if sum of areas is equal to area of triangle, then point is inside
    return Math.abs((areaPBC + areaPAC + areaPAB) - areaABC) < .1;
}

function nearestPointOnLineSegment(point: Point, line: Line): Point {
    const [p1, p2] = line;

    // Calculate the vector from p1 to p2
    const v = {x: p2.x - p1.x, y: p2.y - p1.y};

    // Calculate the dot product of the vector from p1 to point and vector p1 to p2
    const dotProduct = (point.x - p1.x) * v.x + (point.y - p1.y) * v.y;

    // If the dot product is negative, the nearest point is p1
    if (dotProduct <= 0) return p1;

    // Calculate the squared length of the line segment
    const squaredLength = v.x * v.x + v.y * v.y;

    // If the dot product is greater than the squared length, the nearest point is p2
    if (dotProduct >= squaredLength) return p2;

    // Calculate the parametric value of the projection
    const t = dotProduct / squaredLength;

    // Calculate the nearest point on the line segment
    return {x: p1.x + t * v.x, y: p1.y + t * v.y};
}

function nearestPointOnTriangleOutline(point: Point, triangle: Triangle): Point {
    const [p1, p2, p3] = triangle;

    const lines: Line[] = [
        [p1, p2],
        [p2, p3],
        [p3, p1]
    ];

    const nearestPoints = lines.map(line => nearestPointOnLineSegment(point, line));

    const points = [
        ...nearestPoints,
        ...triangle
    ];

    const distances = points.map(p => distance(point, p));
    const minIndex = distances.indexOf(Math.min(...distances));

    // Return the vertex with the minimum distance
    return points[minIndex];
}

function constrainPointToTriangle(point: Point, triangle: Triangle): Point {
    if (pointInsideTriangle(point, triangle)) {
        return point;
    } else {
        return nearestPointOnTriangleOutline(point, triangle);
    }
}

function signedDistanceToLine(point: Point, line: Line): number {
    const [p1, p2] = line;

    const v = {x: p2.x - p1.x, y: p2.y - p1.y}; // Vector along the line
    const w = {x: point.x - p1.x, y: point.y - p1.y}; // Vector from p1 to the point

    const t = (v.x * w.x + v.y * w.y) / (v.x * v.x + v.y * v.y); // Parametric value of projection

    // Calculate the projection of the point onto the line
    const projection = {x: p1.x + t * v.x, y: p1.y + t * v.y};

    const perpendicularDistance = distance(point, projection);

    // Determine the sign of the distance using cross product
    const crossProduct = (v.x * w.y - v.y * w.x);
    const sign = crossProduct > 0 ? 1 : -1;

    return sign * perpendicularDistance;
}

let size_ = 88; //112;
let sizeRef = 120;

let scaling = 4;

let size = size_ * scaling;
let sizeH = size / 2;
let sw = .2 / size_ * sizeRef; //.16 .085
let innerRadius2 = sizeH * (1 - sw / 2);
let innerRadius = sizeH * (1 - sw);
let triangleSizeF = .98; //.76; //.77;
let triangleInnerF = .98; //.6; //.54; //.6;
let triangleKnobF = .52; //.52; //.665;

let conicOffset = .05;
let conicGreyOffset = .12;
let conicGrey = ["#080808", "#f8f8f8"]
let linearOffset = [.12, .05];

const getDetailsOpacity = (zoom: number) => {
    if (zoom < 1.6) return 0;
    if (zoom > 1.8) return 1;
    return (zoom - 1.6) / .2;
}

const ConnectedLabels = ({hue}: { hue: number }) => {
    const zoom = useZoom();
    const style = useStyle();
    const color = style.components.Node.accentColor;

    const opacity = getDetailsOpacity(zoom);
    if (opacity === 0) return null;

    const centeredStyle: CSSProperties = {
        position: "absolute",
        left: sizeH,
        top: sizeH,
        width: 0,
        height: 0,
        pointerEvents: "none"
    }

    const edgeStyle: CSSProperties = {
        ...centeredStyle,
        transform: "rotate(" + hue + "deg)"
    }

    const m120EdgeStyle: CSSProperties = {
        ...centeredStyle,
        transform: "rotate(" + (hue - 120) + "deg)"
    }

    const p120EdgeStyle: CSSProperties = {
        ...centeredStyle,
        transform: "rotate(" + (hue + 120) + "deg)"
    }

    const textStyle: CSSProperties = {
        color,
        fontSize: 7 * scaling,
        textAlign: "center",
        lineHeight: 6 * scaling + "px"
    }

    return <div style={{opacity}}>
        <div style={edgeStyle}>
            <div style={{
                position: "absolute",
                left: -innerRadius * triangleInnerF * 0.866025,
                top: innerRadius * triangleInnerF * .55 - 1,
                width: innerRadius * triangleInnerF * 2 * 0.86602,
                height: innerRadius * .24,
                ...textStyle,
                transform: hue > 90 && hue < 270 ? "rotate(180deg)" : undefined
            }}>lightness
            </div>
        </div>
        <div style={p120EdgeStyle}>
            <div style={{
                position: "absolute",
                left: -innerRadius * triangleInnerF * 0.866025,
                top: innerRadius * triangleInnerF * .55 - 1,
                width: innerRadius * triangleInnerF * 2 * 0.86602,
                height: innerRadius * .24,
                ...textStyle,
                transform: hue < 150 || hue > 330 ? "rotate(180deg)" : undefined
            }}>saturation
            </div>
        </div>
        <div style={centeredStyle}>
            <div style={{
                position: "absolute",
                left: -innerRadius * triangleInnerF * 0.866025,
                bottom: sizeH,
                width: innerRadius * triangleInnerF * 2 * 0.86602,
                height: innerRadius * .24,
                ...textStyle
            }}>hue
            </div>
        </div>
    </div>
}

function hslToRgb(h: number, s: number, l: number) {
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hueToRgb(p, q, h + 1 / 3);
        g = hueToRgb(p, q, h);
        b = hueToRgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function hueToRgb(p: number, q: number, t: number) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
}

function getLuminance(color: string) {
    let luminance = 255;

    const rgbMatch = color.match(/rgb\((\d+), (\d+), (\d+)\)/);
    if (rgbMatch) {
        const [, r, g, b] = rgbMatch;
        let red = parseInt(r);
        let green = parseInt(g);
        let blue = parseInt(b);
        luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    } else {
        const hslMatch = color.match(/hsl\((\d+), (\d+)%, (\d+)%\)/);
        if (hslMatch) {
            const [, h, s, l] = hslMatch;
            let hue = parseInt(h);
            let saturation = parseInt(s);
            let lightness = parseInt(l);
            let [red, green, blue] = hslToRgb(hue / 360, saturation / 100, lightness / 100);
            luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
        } else {
            const hexMatch = color.match(/#([0-9a-fA-F]{6})/);
            if (hexMatch) {
                const [, hex] = hexMatch;
                let red = parseInt(hex.slice(0, 2), 16);
                let green = parseInt(hex.slice(2, 4), 16);
                let blue = parseInt(hex.slice(4, 6), 16);
                luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
            }
        }
    }

    luminance /= 255;
    return luminance;
}

const FancyKnobLabel = (props: {
    x: number,
    y: number,
    label: string,
    unit?: string,
    onChange?: (v: string) => number | null,
    on?: boolean, style?: CSSProperties,
    scaling: number
}) => {
    const zoom = useZoom();
    const style = useStyle();
    const breakPoint = style.components.Input.colorBreakpoint;
    const inputColorLight = style.components.Input.colorLight;
    const inputColorDark = style.components.Input.colorDark;
    const [color, setColor] = useState<string>(props.style?.color ?? style.components.Input.color);
    const hasColor = !!props.style?.color;

    const [hover, setHover] = useState<boolean>(false);
    const [stay, setStay] = useState<boolean>(false);
    const [stringValue, setStringValue] = useState<string>(props.label);

    const ref = useRef<HTMLInputElement>(null);

    const background = props.style?.background;
    useEffect(() => {
        if (props.style?.color) {
            setColor(props.style?.color);
        }
    }, [props.style?.color]);

    useEffect(() => {
        if (hasColor) return;
        if (background) {
            if (typeof background === "string") {
                let luminance = getLuminance(background);
                setColor(luminance < breakPoint ? inputColorLight : inputColorDark);
            }
        }
    }, [background]);

    useEffect(() => {
        setStringValue(props.label);
        setTimeout(() => ref.current?.focus(), 100);
    }, [props.label, props.on])

    const handleStay = () => {
        setStay(false);
    }

    useEffect(() => {
        if (stay) {
            window.addEventListener('mousemove', handleStay);
            return () => window.removeEventListener('mousemove', handleStay);
        }
    }, [stay]);

    const handleLabelMouseEnter = () => {
        setHover(true);
    }

    const handleLabelMouseLeave = () => {
        setHover(false);
    }

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setStringValue(e.target.value);
    }

    const handleCommit = () => {
        setStay(true);
        let r = props.onChange?.(stringValue);
        if (r === null) {
            setStringValue(props.label);
        } else {
            setStringValue("" + r);
        }
    }

    return <AnimatePresence>
        {(stay || (hover || props.on)) && <div style={{
            position: "absolute",
            left: props.x,
            top: props.y,
            transform: "scale(" + (1.25 / zoom) + ")",
            zIndex: 9999
        }}>
            <motion.div
                key={"label"}
                style={{
                    position: "absolute",
                    left: 0,
                    bottom: 8,
                    width: "auto",
                    height: 78,
                    borderRadius: 12,
                    pointerEvents: "none",
                    opacity: hover ? 1 : 0,
                    fontSize: 14 * props.scaling,
                    color,
                    fontFamily: 'Roboto Mono',
                    fontWeight: 700,
                    textAlign: "center",
                    paddingLeft: 16,
                    paddingRight: 16,
                    transform: "translateX(-50%)",
                    overflow: "hidden"
                    //cursor: "text"
                }}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                onMouseEnter={handleLabelMouseEnter}
                onMouseLeave={handleLabelMouseLeave}
            >
                <div style={{
                    position: "absolute",
                    inset: 0,
                    background: props.style?.background,
                    opacity: .5,
                    zIndex: 0
                }}/>
                <span style={{position: "relative", zIndex: 1, opacity: 0}}>
                    {stringValue + (props.unit ?? "")}
                </span>
                <div style={{
                    position: "absolute",
                    left: 16,
                    right: 0,
                    bottom: 0,
                    top: 0,
                    zIndex: 0
                }}>
                    <InputBase
                        ref={ref}
                        key={"knob label " + props.label}
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "transparent",
                            //width: 'auto',
                            fontFamily: 'Roboto Mono',
                            fontSize: 14 * props.scaling,
                            fontWeight: 400,
                            lineHeight: '14px',
                            color,
                            width: "100%",
                            height: "100%",
                            zIndex: 99999,
                            pointerEvents: "none",
                            cursor: "text",
                            textAlign: "left"
                        }}
                        type="text"
                        value={stringValue}
                        onChange={handleChange}
                        /*onBlur={handleTyperBlur}*/
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                handleCommit();
                            } else if (e.key === 'Escape') {
                                setStringValue(props.label);
                            }
                        }}
                        autoFocus
                    />
                    {props.unit && <div style={{
                        position: "absolute",
                        right: 16,
                        top: 0,
                        height: "100%",
                        zIndex: 1,
                        pointerEvents: "none"
                    }}>{props.unit}</div>}
                </div>
            </motion.div>
        </div>}
    </AnimatePresence>
}

const FancyKnobMultiLabel = (props: {
    x: number,
    y: number,
    label: string[],
    unit?: string[],
    name?: string[],
    onChange?: (v: string, i: number) => number | null,
    on?: boolean,
    style?: CSSProperties,
    scaling: number
}) => {
    const zoom = useZoom();
    const style = useStyle();
    const breakPoint = style.components.Input.colorBreakpoint;
    const inputColorLight = style.components.Input.colorLight;
    const inputColorDark = style.components.Input.colorDark;
    const [color, setColor] = useState<string>(props.style?.color ?? style.components.Input.color);
    const [active, setActive] = useState<number>(0);
    const hasColor = !!props.style?.color;

    const [hover, setHover] = useState<boolean>(false);
    const [stay, setStay] = useState<boolean>(false);
    const [stringValues, setStringValues] = useState<string[]>(props.label);

    const background = props.style?.background;
    useEffect(() => {
        if (props.style?.color) {
            setColor(props.style?.color);
        }
    }, [props.style?.color]);

    useEffect(() => {
        if (hasColor) return;
        if (background) {
            if (typeof background === "string") {
                let luminance = getLuminance(background);
                setColor(luminance < breakPoint ? inputColorLight : inputColorDark);
            }
        }
    }, [background]);

    useEffect(() => {
        setStringValues([...props.label]);
        //setTimeout(() => ref.current?.focus(), 100);
    }, [props.label, props.on])

    useEffect(() => {
        console.log(stringValues)
    }, [stringValues])

    const handleLabelMouseEnter = () => {
        setHover(true);
    }

    const handleLabelMouseLeave = () => {
        setHover(false);
    }

    const handleChanges = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => handleChange(e, active)
    const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, i: number) => {
        setStringValues(s => {
            s[i] = e.target.value;
            return [...s]
        });
    }

    const handleCommit = (i: number) => {
        let r = props.onChange?.(stringValues[i], i);
        if (r === null) {
            setStringValues([...props.label]);
        } else {
            setStringValues(s => {
                s[i] = "" + r;
                return [...s]
            });
        }
    }

    return <AnimatePresence>
        {(hover || props.on) && <div style={{
            position: "absolute",
            left: props.x,
            top: props.y,
            transform: "scale(" + (1.25 / zoom) + ")",
            zIndex: 9999
        }}>
            <motion.div
                key={"label"}
                style={{
                    position: "absolute",
                    left: 0,
                    bottom: 8,
                    width: "auto",
                    height: 78 * props.label.length,
                    borderRadius: 12,
                    pointerEvents: "none",
                    opacity: hover ? 1 : 0,
                    fontSize: 14 * props.scaling,
                    color,
                    fontFamily: 'Roboto Mono',
                    fontWeight: 700,
                    textAlign: "center",
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center"
                    //cursor: "text"
                }}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0/*, transition: {delay: 5000}*/}}
                onMouseEnter={handleLabelMouseEnter}
                onMouseLeave={handleLabelMouseLeave}
            >
                {props.label.map((l, i) => {
                    return <div key={"knob label wrapper " + (active === i ? "a" : i)} style={{
                        position: "relative",
                        height: 78,
                        maxHeight: 78,
                        minHeight: 78,
                        margin: 0,
                        width: "auto",
                        paddingLeft: 16,
                        paddingRight: 16,
                        paddingTop: 0,
                        paddingBottom: 0,
                        opacity: active === i ? 1 : .5,
                        zIndex: 0
                    }}>
                        <div style={{
                            position: "absolute",
                            inset: 0,
                            background: props.style?.background,
                            borderRadius: 12,
                            opacity: .667, //active === i ? .667 : .333,
                            zIndex: 0
                        }}/>
                        <div style={{
                            display: "flex",
                            flexDirection: "row",
                            fontFamily: 'Roboto Mono',
                            fontSize: 14 * props.scaling,
                            fontWeight: 400,
                            height: 78,
                            lineHeight: "78px", //`${14 * props.scaling}px`,
                            zIndex: 1,
                            //opacity: active === i ? 0 : .5,
                            whiteSpace: "nowrap"
                        }}>
                            {props.name?.[i] && <div style={{position: "relative", width: "auto", height: 78}}>{props.name?.[i] + ":"}&nbsp;</div>}
                            <div style={{position: "relative", width: "auto", height: 78}}>
                                <span style={{
                                    position: "relative",
                                    height: 78,
                                    display: "inline-block",
                                    lineHeight: `${14 * props.scaling}px`,
                                    zIndex: 1,
                                    opacity: active === i ? 0 : 1,
                                    whiteSpace: "nowrap"
                                }}>
                                    {stringValues[i]}
                                </span>
                                {active === i && <div style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 0
                                }}>
                                    <InputBase
                                        key={"knob label " + l}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            background: "transparent",
                                            //width: 'auto',
                                            fontFamily: 'Roboto Mono',
                                            fontSize: 14 * props.scaling,
                                            fontWeight: 400,
                                            lineHeight: '14px',
                                            color,
                                            width: "100%",
                                            height: "100%",
                                            zIndex: 99999,
                                            pointerEvents: "none",
                                            cursor: "text",
                                            padding: 0
                                        }}
                                        type="text"
                                        value={stringValues[i]}
                                        onChange={handleChanges}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                handleCommit(i);
                                            } else if (e.key === 'Escape') {
                                                setStringValues(props.label);
                                            } else if (e.key === 'ArrowUp') {
                                                setActive(a => a === 0 ? props.label.length - 1 : a - 1);
                                                e.preventDefault();
                                                e.stopPropagation();
                                            } else if (e.key === 'ArrowDown') {
                                                setActive(a => a === props.label.length - 1 ? 0 : a + 1);
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                        autoFocus
                                    />
                                </div>}
                            </div>
                            {props.unit?.[i] && <div style={{
                                position: "relative",
                                width: "auto",
                                height: 78,
                                pointerEvents: "none"
                            }}>{props.unit[i]}</div>}
                        </div>
                    </div>
                })}
            </motion.div>
        </div>}
    </AnimatePresence>
}

const FancyKnob = (props: {
    size: "big" | "small" | "color",
    scaling: number,
    transform?: string,
    onDrag: DraggableEventHandler,
    x: number,
    y: number,
    style?: CSSProperties,
    label?: string | string[],
    unit?: string | string[],
    name?: string | string[],
    onChange?: (v: string, index?: number) => number | null,
    onDragStart?: () => any,
    onDragEnd?: () => any
}) => {
    const dragRef = useRef<HTMLDivElement>(null);
    const [hover, setHover] = useState<boolean>(false);
    const [dragging, setDragging] = useState<boolean>(false);
    const [stay, setStay] = useState<boolean>(false);

    const {x, y, style} = props;
    const multi = Array.isArray(props.label);

    const knobSizes = {
        big: 36 * props.scaling / 2,
        small: 24 * props.scaling / 2,
        color: 18 * props.scaling / 2
    }
    const auraSizes = {
        big: [1.8, 2.4],
        small: [2.4, 3.1],
        color: [2.6, 3.8]
    }
    const knobSize = knobSizes[props.size];
    const areaSize = auraSizes[props.size];

    const onMouseEnter = () => {
        setHover(true);
    }

    const onMouseLeave = () => {
        setHover(false);
    }

    const motionDivProps = {
        initial: {scale: 1},
        whileHover: {scale: areaSize[0]},
        whileTap: {scale: areaSize[1]},
        onMouseEnter,
        onMouseLeave
    }

    const handleDragStart = () => {
        setDragging(true);
        props.onDragStart?.();
    }

    const handleDragStop = () => {
        setDragging(false);
        setHover(false);
        props.onDragEnd?.();
    }

    return <DraggableCore
        nodeRef={dragRef}
        onDrag={hover ? props.onDrag : undefined}
        onStart={handleDragStart}
        onStop={handleDragStop}>
        <div
            ref={dragRef}
            style={{
                position: "absolute",
                left: x - knobSize / 2,
                top: y - knobSize / 2,
                width: knobSize,
                height: knobSize,
                borderRadius: knobSize / 2,
                cursor: "pointer",
                pointerEvents: "none",
                ...style
            }}
        >
            <motion.div
                key={"color wheel h knob"}
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: knobSize,
                    height: knobSize,
                    borderRadius: knobSize / 2,
                    cursor: "pointer",
                    pointerEvents: "auto",
                    ...style,
                    opacity: .2,
                    zIndex: 9999
                }}
                {...motionDivProps}
            >{dragging && <div style={{
                position: "absolute",
                left: -1000,
                top: -1000,
                width: 2000,
                height: 2000,
                pointerEvents: "auto",
                zIndex: 8888
            }}/>}</motion.div>
            <div style={{
                position: "absolute",
                inset: 0,
                //zIndex: 1,
                pointerEvents: "none",
                boxShadow: "inset 0 0 0 6px " + (style?.color ?? "#fff"),
                borderRadius: knobSize / 2
            }}/>
            {props.label && <div
                style={{
                    position: "absolute",
                    left: knobSize / 2,
                    top: knobSize / 2,
                    width: 0,
                    height: 0,
                    transform: props.transform,
                    zIndex: 9
                }}
            >
                {multi ? <FancyKnobMultiLabel
                    onChange={props.onChange}
                    x={0}
                    y={-knobSize / 2}
                    label={props.label as string[]}
                    unit={props.unit as string[]}
                    name={props.name as string[]}
                    on={hover || stay}
                    style={style}
                    scaling={props.scaling}
                /> : <FancyKnobLabel
                    onChange={props.onChange}
                    x={0}
                    y={-knobSize / 2}
                    label={props.label as string}
                    unit={props.unit as string}
                    on={hover || stay}
                    style={style}
                    scaling={props.scaling}
                />}
            </div>}
        </div>
    </DraggableCore>
}

const ColorWheel = (props: {
    hue: number,
    saturation: number,
    lightness: number,
    lockHue?: boolean,
    lockSaturation?: boolean,
    lockLightness?: boolean,
    onChange: (value: { h?: number, s?: number, l?: number }) => void,
}) => {
    let externalHue = props.hue;
    let externalSaturation = props.saturation;
    let externalLightness = props.lightness;

    let [fadeHue, setFadeHue] = useState<number | null>(null);
    let [fadeSaturation, setFadeSaturation] = useState<number | null>(null);
    let [fadeLightness, setFadeLightness] = useState<number | null>(null);

    let [internalHue, setInternalHue] = useState<number | null>(null);
    let [internalSaturation, setInternalSaturation] = useState<number | null>(null);
    let [internalLightness, setInternalLightness] = useState<number | null>(null);

    let lockHue = !!props.lockHue;
    let lockSaturation = !!props.lockSaturation;
    let lockLightness = !!props.lockLightness;

    let hue = internalHue === null || lockHue ? externalHue : internalHue;
    let saturation = internalSaturation === null ? externalSaturation : internalSaturation;
    let lightness = internalLightness === null ? externalLightness : internalLightness;

    const style = useStyle();
    const {colorBreakpoint, colorLight, colorDark} = style.components.Input;
    const disabledColor = style.components.Port.disabled?.colorValue;

    const setSaturationAndLightness = (s: number, l: number) => {
        if (lockSaturation && lockLightness) return;

        const result: {s?: number, l?: number} = {s: Math.floor(s), l: Math.floor(l)};
        if (lockSaturation) {
            delete result.s;
        }
        if (lockLightness) {
            delete result.l
        }

        props.onChange(result);
    }

    const setHue = (h: number) => {
        if (lockHue) return;

        props.onChange({h: Math.floor(h)});
    }

    const fps = 30, speed = .4;

    useEffect(() => {
        if (fadeHue !== null) {
            let d = (hue - fadeHue);
            if (d > 180) d -= 360;
            if (d < -180) d += 360;

            if (Math.abs(d) < 1) {
                setFadeHue(null);
            } else {
                setTimeout(() => {
                    let result = (fadeHue !== null) ? fadeHue + d * speed : null;
                    if (result !== null) {
                        if (result < 0) result += 360;
                        if (result >= 360) result -= 360;
                    }
                    setFadeHue(result);
                }, fps);
            }
        }
    }, [fadeHue]);

    useEffect(() => {
        if (fadeSaturation !== null) {
            let d = (saturation - fadeSaturation);
            if (d > 180) d -= 360;
            if (d < -180) d += 360;

            if (Math.abs(d) < 1) {
                setFadeSaturation(null);
            } else {
                setTimeout(() => {
                    let result = (fadeSaturation !== null) ? fadeSaturation + d * speed : null;
                    if (result !== null) {
                        if (result < 0) result += 360;
                        if (result >= 360) result -= 360;
                    }
                    setFadeSaturation(result);
                }, fps);
            }
        }
    }, [fadeSaturation]);

    useEffect(() => {
        if (fadeLightness !== null) {
            let d = (lightness - fadeLightness);
            if (d > 180) d -= 360;
            if (d < -180) d += 360;

            if (Math.abs(d) < 1) {
                setFadeLightness(null);
            } else {
                setTimeout(() => {
                    let result = (fadeLightness !== null) ? fadeLightness + d * speed : null;
                    if (result !== null) {
                        if (result < 0) result += 360;
                        if (result >= 360) result -= 360;
                    }
                    setFadeLightness(result);
                }, fps);
            }
        }
    }, [fadeLightness]);

    const handleDrag: DraggableEventHandler = (e, data) => {
        if (lockHue) return;

        let {x, y} = data;
        let a = Math.atan2(y, x) * 180 / Math.PI;
        a += 90;
        if (a > 360) a -= 360;
        if (a < 0) a += 360;
        setInternalHue(a);
        setHue(a);
    }

    const handleDragTri: DraggableEventHandler = (e, data) => {
        if (lockSaturation && lockLightness) return;
        let {x, y} = data;
        const zoom = store.getState().transform.zoom;
        x /= zoom / scaling;
        y /= zoom / scaling;

        let tri: Triangle = getTriangle(0, 0, innerRadius2 * triangleKnobF, hue);
        let cstr = constrainPointToTriangle({x, y}, tri);
        x = cstr.x;
        y = cstr.y;

        let base: [Point, Point] = [tri[1], tri[2]];
        let baseMid = {x: (base[0].x + base[1].x) / 2, y: (base[0].y + base[1].y) / 2};
        let hLine: [Point, Point] = [baseMid, tri[0]];
        let hDist = -signedDistanceToLine({x, y}, hLine);

        let a = distance(base[0], base[1]);
        let h = -signedDistanceToLine({x, y}, base) / 0.86602540378;
        if (h < 0) h = 0;
        if (h > a) h = a;

        let rS = h / a;
        let rL_ = hDist / a;
        if (rL_ < -.5) rL_ = -.5;
        if (rL_ > .5) rL_ = .5;
        let rLa = 1 - 2 * Math.abs(rL_);
        if (rLa === 0) {
            rS = 0;
        } else {
            if (!lockLightness)
            rS /= rLa;
        }
        let rL = rL_ + .5;
        if (rS < 0) rS = 0;
        if (rS > 1) rS = 1;

        let ns = rS * 100, nl = rL * 100;
        if (!lockSaturation) setInternalSaturation(ns);
        if (!lockLightness) setInternalLightness(nl);
        setSaturationAndLightness(ns, nl);
    }

    const handleStartHue = () => {
        setInternalHue(hue);
    }

    const handleEndHue = () => {
        setInternalHue(null);
    }

    const handleStartSaturationAndLightness = () => {
        if (!lockSaturation) setInternalSaturation(saturation);
        if (!lockLightness) setInternalLightness(lightness);
    }

    const handleEndSaturationAndLightness = () => {
        setInternalSaturation(null);
        setInternalLightness(null);
    }

    const handleChangeHueByText = (v: string) => {
        let h = parseInt(v);
        if (isNaN(h)) return null;
        if (h < 0) h = h % 360 + 360;
        if (h >= 360) h = h % 360;
        setFadeHue(hue);
        props.onChange({h});
        return h;
    }

    const handleChangeSaturationLightnessByText = (v: string, index?: number) => {
        if (index === 0) {
            if (lockSaturation) return null;

            let s = parseInt(v);
            if (isNaN(s)) return null;
            if (s < 0) s = 0;
            if (s > 100) s = 100;
            setFadeSaturation(saturation);
            props.onChange({s});
            return s;
        } else {
            if (lockLightness) return null;

            let l = parseInt(v);
            if (isNaN(l)) return null;
            if (l < 0) l = 0;
            if (l > 100) l = 100;
            setFadeLightness(lightness);
            props.onChange({l});
            return l;
        }
    }

    const centeredStyle: CSSProperties = {
        position: "absolute",
        left: sizeH,
        top: sizeH,
        width: 0,
        height: 0
    }

    const edgeStyle: CSSProperties = {
        ...centeredStyle,
        transform: "rotate(" + (fadeHue === null ? hue : fadeHue) + "deg)"
    }

    const m120EdgeStyle: CSSProperties = {
        ...centeredStyle,
        transform: "rotate(" + ((fadeHue === null ? hue : fadeHue) - 120) + "deg)"
    }

    const p120EdgeStyle: CSSProperties = {
        ...centeredStyle,
        transform: "rotate(" + ((fadeHue === null ? hue : fadeHue) + 120) + "deg)"
    }

    const edgeChildStyle: CSSProperties = {
        position: "absolute",
        left: -innerRadius * triangleInnerF * 0.866025,
        top: innerRadius * triangleInnerF * .5 - 1,
        width: innerRadius * triangleInnerF * 2 * 0.86602,
        height: innerRadius * (triangleSizeF - triangleInnerF) / 2 + 1,
    }

    const pointChildStyle: CSSProperties = {
        position: "absolute",
        left: -innerRadius * (triangleSizeF - triangleInnerF) / 2,
        top: -innerRadius * triangleInnerF - innerRadius * (triangleSizeF - triangleInnerF) / 2,
        width: innerRadius * (triangleSizeF - triangleInnerF),
        height: innerRadius * (triangleSizeF - triangleInnerF),
        borderRadius: "50%",
    }

    const aura = false;

    const background = `hsl(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%)`;
    const luminance = getLuminance(background);
    const color = luminance < colorBreakpoint ? colorLight : colorDark;

    const disabledStyle: CSSProperties = {
        pointerEvents: "none",
        color: disabledColor
    }

    return <div style={{
        width: 0, height: size / 2,
        transform: `scale(${1 / scaling}) translate(-50%, -50%)`
    }}>
        <div style={{
            position: "absolute", left: 0, top: 0, width: size, height: size,
            clipPath: "polygon(" + getCircleHoleClipPath(innerRadius / size * 100) + ")",
            borderRadius: "50%",
            background: "conic-gradient(" + getColorWheelSteps(12, (fadeSaturation === null ? saturation : fadeSaturation), (fadeLightness === null ? lightness : fadeLightness)).join(", ") + ")"
        }}/>
        <div style={{
            position: "absolute",
            left: sizeH - innerRadius * triangleInnerF,
            top: sizeH - innerRadius * triangleInnerF,
            width: innerRadius * triangleInnerF * 2,
            height: innerRadius * triangleInnerF * 2,
            transform: "rotate(" + (fadeHue === null ? hue : fadeHue) + "deg)",
            clipPath: "polygon(" + getTriangleClipPath(50, 50, 50, 0) + ")",
            background: `linear-gradient(hsla(${(fadeHue === null ? hue : fadeHue)}, 100%, 50%, 1) ${linearOffset[0] * 75}%, hsla(${(fadeHue === null ? hue : fadeHue)}, 100%, 50%, 0) ${75 - linearOffset[1] * 75}%), conic-gradient(from 0deg at 50% 0%, #000000 150deg, #000000 ${150 + 60 * conicOffset}deg, ${conicGrey[0]} ${150 + 60 * conicGreyOffset}deg, ${conicGrey[1]} ${210 - 60 * conicGreyOffset}deg, #ffffff ${210 - 60 * conicOffset}deg, #ffffff 210deg)`
        }}/>
        {aura && <>
            <div style={{
                ...m120EdgeStyle,
                zIndex: -1
            }}>
                <div style={{
                    ...pointChildStyle,
                    background: `#fff`
                }}/>
            </div>
            <div style={{
                ...edgeStyle,
                zIndex: -1
            }}>
                <div style={{
                    ...pointChildStyle,
                    background: `hsl(${hue}, 100%, 50%)`
                }}/>
            </div>
            <div style={{
                ...p120EdgeStyle,
                zIndex: -1
            }}>
                <div style={{
                    ...pointChildStyle,
                    background: `#000`
                }}/>
            </div>
            <div style={edgeStyle}>
                <div style={{
                    ...edgeChildStyle,
                    background: `linear-gradient(270deg, hsl(${hue}, 0%, 0%), hsl(${hue}, 0%, 100%))`
                }}/>
            </div>
            <div style={p120EdgeStyle}>
                <div style={{
                    ...edgeChildStyle,
                    background: `linear-gradient(90deg, hsl(${hue}, 100%, 50%), hsl(${hue}, 100%, 100%))`
                }}/>
            </div>
            <div style={m120EdgeStyle}>
                <div style={{
                    ...edgeChildStyle,
                    background: `linear-gradient(270deg, hsl(${hue}, 100%, 50%), hsl(${hue}, 100%, 0%))`
                }}/>
            </div>
        </>}
        <ConnectedLabels hue={(fadeHue === null ? hue : fadeHue)}/>
        <svg viewBox={`-${sizeH} -${sizeH} ${size} ${size}`} style={{
            position: "absolute", left: 0, top: 0, width: size, height: size
        }}>
            <g stroke={"#00306030"} strokeWidth={.25 * scaling} fillOpacity={0}>
                <circle cx={0} cy={0} r={innerRadius}/>
                <circle cx={0} cy={0} r={size / 2}/>
                <path d={getTrianglePath(0, 0, innerRadius * triangleInnerF, (fadeHue === null ? hue : fadeHue))}/>
            </g>
        </svg>
        <div style={{position: "absolute", left: sizeH, top: sizeH, width: 0, height: 0}}>
            <FancyKnob
                onDrag={handleDrag}
                size={"color"}
                scaling={scaling}
                x={Math.sin((fadeHue === null ? hue : fadeHue) * Math.PI / 180) * innerRadius2}
                y={-Math.cos((fadeHue === null ? hue : fadeHue) * Math.PI / 180) * innerRadius2}
                style={{background, color, ...(lockHue ? disabledStyle : {})}}
                label={"" + Math.round(hue)}
                unit={"°"}
                onChange={handleChangeHueByText}
                onDragStart={handleStartHue}
                onDragEnd={handleEndHue}
            />
            <div style={{position: "absolute", transform: "rotate(" + (fadeHue === null ? hue : fadeHue) + "deg)", left: 0, top: 0}}>
                <FancyKnob
                    onDrag={handleDragTri}
                    size={"color"}
                    scaling={scaling}
                    transform={"rotate(" + -hue + "deg)"}
                    x={-((fadeLightness === null ? lightness : fadeLightness) - 50) / 100 * innerRadius2 * triangleKnobF * 0.86 * 2}
                    y={innerRadius2 * triangleKnobF * (.5 - (1 - Math.abs((fadeLightness === null ? lightness : fadeLightness) - 50) / 50) * (fadeSaturation === null ? saturation : fadeSaturation) * 1.5 / 100)}
                    style={{background, color, ...(lockSaturation && lockLightness ? disabledStyle : {})}}
                    label={["" + Math.round(saturation), "" + Math.round(lightness)]}
                    unit={["%", "%"]}
                    name={["s", "l"]}
                    onChange={handleChangeSaturationLightnessByText}
                    onDragStart={handleStartSaturationAndLightness}
                    onDragEnd={handleEndSaturationAndLightness}
                />
            </div>
        </div>
    </div>
}

export default ColorWheel;